package com.merkost.handpass.admin.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.merkost.handpass.admin.api.AdminApiClient
import com.merkost.handpass.admin.auth.LocalAdminAuth
import com.merkost.handpass.admin.auth.SupabaseAuthClient
import com.merkost.handpass.admin.container.ToastHostContainer
import com.merkost.handpass.admin.contract.ToastHostIntent
import com.merkost.handpass.admin.contract.ToastKind
import com.merkost.handpass.admin.di.koinContainer
import com.merkost.handpass.admin.util.LocalViewportMode
import com.merkost.handpass.admin.util.ViewportMode
import com.merkost.handpass.admin.util.rememberViewportMode
import com.merkost.handpass.sharedweb.components.WebIcon
import com.merkost.handpass.sharedweb.components.Icon
import com.merkost.handpass.sharedweb.components.WebDrawer
import com.merkost.handpass.sharedweb.theme.Bg
import com.merkost.handpass.sharedweb.theme.WebFont
import com.merkost.handpass.sharedweb.theme.WebType
import com.merkost.handpass.sharedweb.theme.HandpassSpacing
import com.merkost.handpass.sharedweb.theme.OnSurface
import com.merkost.handpass.sharedweb.theme.OnSurfaceMuted
import com.merkost.handpass.sharedweb.theme.PadLg
import com.merkost.handpass.sharedweb.theme.RadiusControl
import com.merkost.handpass.sharedweb.theme.SurfaceMuted
import com.merkost.handpass.sharedweb.theme.TouchTarget
import com.merkost.handpass.sharedweb.theme.transitionColors
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toAttrs
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.fontWeight
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.minHeight
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLButtonElement

val MenuIconButtonStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.LegacyInlineFlex)
            .padding(HandpassSpacing.SM)
            .borderRadius(RadiusControl.value())
            .color(OnSurfaceMuted.value())
            .cursor(Cursor.Pointer)
            .transitionColors()
            .styleModifier {
                property("min-width", "${TouchTarget.value()}")
                property("min-height", "${TouchTarget.value()}")
                property("align-items", "center")
                property("justify-content", "center")
                property("background", "transparent")
                property("border", "none")
            }
    }
    hover {
        Modifier
            .backgroundColor(SurfaceMuted.value())
            .color(OnSurface.value())
    }
}

interface AdminPageScope {
    fun navigate(path: String)
    fun toast(message: String, kind: ToastKind = ToastKind.Success)
    fun showUndoToast(
        message: String,
        onUndo: () -> Unit,
        undoLabel: String = "Undo",
        kind: ToastKind = ToastKind.Success,
        durationMs: Int = 8000,
    )
}

@Composable
fun AdminPageScaffold(
    title: String,
    currentPath: String,
    showTitle: Boolean = true,
    content: @Composable AdminPageScope.() -> Unit,
) {
    val ctx = rememberPageContext()
    val authState = LocalAdminAuth.current
    val toastHost = koinContainer<ToastHostContainer>()
    val scope = rememberCoroutineScope()

    val pageScope = object : AdminPageScope {
        override fun navigate(path: String) { ctx.router.navigateTo(path) }
        override fun toast(message: String, kind: ToastKind) {
            toastHost.store.intent(ToastHostIntent.Show(message, kind))
        }
        override fun showUndoToast(message: String, onUndo: () -> Unit, undoLabel: String, kind: ToastKind, durationMs: Int) {
            toastHost.store.intent(ToastHostIntent.ShowWithAction(message, kind, undoLabel, durationMs, onUndo))
        }
    }

    var confirmLogout by remember { mutableStateOf(false) }

    val performLogout: () -> Unit = {
        scope.launch {
            SupabaseAuthClient.signOut()
            AdminApiClient.accessToken = null
            ctx.router.navigateTo("/")
        }
    }

    val onLogout: () -> Unit = { confirmLogout = true }

    val viewportMode = rememberViewportMode()

    CompositionLocalProvider(LocalViewportMode provides viewportMode) {
        val sidebarOffsetPx = when (viewportMode) {
            ViewportMode.Wide -> 248
            ViewportMode.Compact -> 64
            ViewportMode.Mobile -> 0
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                minHeight(100.vh)
                property("background-color", Bg.value().toString())
                property("font-family", WebFont.Family)
            }
        }) {
            if (viewportMode != ViewportMode.Mobile) {
                Sidebar(
                    currentPath = currentPath,
                    authState = authState,
                    onNavigate = { ctx.router.navigateTo(it) },
                    onLogout = onLogout,
                )
            }

            Div({
                style {
                    property("margin-left", "${sidebarOffsetPx}px")
                    property("flex", "1")
                    if (viewportMode == ViewportMode.Mobile) {
                        property("padding", "16px")
                    } else {
                        property("padding", "${PadLg.value()} clamp(20px, 4vw, 40px)")
                    }
                    property("min-width", "0")
                }
            }) {
                if (viewportMode == ViewportMode.Mobile) {
                    var sidebarOpen by remember { mutableStateOf(false) }
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            property("margin-bottom", HandpassSpacing.MD.toString())
                        }
                    }) {
                        Button(
                            attrs = MenuIconButtonStyle.toAttrs<AttrsScope<HTMLButtonElement>> {
                                onClick { sidebarOpen = !sidebarOpen }
                                attr("aria-label", if (sidebarOpen) "Close navigation menu" else "Open navigation menu")
                                attr("aria-expanded", sidebarOpen.toString())
                            }
                        ) {
                            Icon(WebIcon.Menu, size = 20)
                        }
                        Span({
                            style {
                                property("margin-left", HandpassSpacing.MD.toString())
                                fontSize(18.px)
                                fontWeight(700)
                                property("color", OnSurface.value().toString())
                            }
                        }) { Text(title) }
                    }
                    if (sidebarOpen) {
                        WebDrawer(onDismiss = { sidebarOpen = false }, ariaLabel = "Navigation menu") {
                            Sidebar(
                                currentPath = currentPath,
                                authState = authState,
                                onNavigate = {
                                    sidebarOpen = false
                                    ctx.router.navigateTo(it)
                                },
                                onLogout = onLogout,
                                embedded = true,
                            )
                        }
                    }
                } else if (showTitle) {
                    H1({
                        style {
                            fontSize(WebType.H1)
                            fontWeight(700)
                            property("color", OnSurface.value().toString())
                            property("letter-spacing", "-0.02em")
                            property("margin", "0 0 ${PadLg.value()} 0")
                        }
                    }) { Text(title) }
                }

                pageScope.content()
            }

            AdminToastHost()
            AdminCommandPalette()

            if (confirmLogout) {
                AdminConfirmDialog(
                    config = ConfirmConfig(
                        title = "Sign out",
                        message = "You'll be signed out and need to sign in again to access the admin.",
                        confirmLabel = "Sign out",
                    ),
                    onConfirm = { performLogout() },
                    onDismiss = { confirmLogout = false },
                )
            }
        }
    }
}
